import { Button, Card, Space, Tabs, Tag, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { useAppSelector, useAuth } from '../../store/hooks';
import { AppstoreOutlined, NumberOutlined } from '@ant-design/icons';
import DeleteCompanyModal from './DeleteCompanyModal';
import AddShopifyShopModal from '../ShopifyShop/AddShopifyShopModal';
import CompanyOverviewTab from './CompanySettings/CompanyOverviewTab';
import GeneralSettingsTab from './CompanySettings/GeneralSettingsTab';
import CompanyMembersTab from './CompanySettings/CompanyMembersTab';
import CompanyShopifyTab from './CompanySettings/CompanyShopifyTab';
import WorkspaceModal from '../Layout/WorkspaceModal';
import './CompanySettingsModal.css';

interface CompanySettingsModalProps {
    open: boolean;
    handleClose: () => void;
}

const CompanySettingsModal: React.FC<CompanySettingsModalProps> = ({ open, handleClose }) => {

    const selectedCompany = useAppSelector((state) => state.companies.selectedCompany);
    const { claims, currentUser } = useAuth();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [shopifyModalOpen, setShopifyModalOpen] = useState(false);

    const selectedShopify = selectedCompany ? (selectedCompany as any).shopify || {} : {};
    const myLevel = useMemo(() => {
        const companyAccess = claims?.companyAccess || {};
        if (claims?.superadmin) return 100;
        if (!selectedCompany) return 0;
        const levelFromClaims = companyAccess[selectedCompany.id] ?? 0;
        const levelFromDoc = selectedCompany?.accessLevels?.[currentUser?.uid ?? ''] ?? 0;
        return Math.max(levelFromClaims, levelFromDoc, companyAccess['*'] ?? 0);
    }, [claims?.companyAccess, claims?.superadmin, selectedCompany, currentUser?.uid]);
    const canManageCompany = !!selectedCompany && (claims?.superadmin || myLevel >= 80);

    const enabledModules = selectedCompany?.modules
        ? Object.entries(selectedCompany.modules).filter(([, enabled]) => enabled).map(([key]) => key)
        : [];

    return (
        <WorkspaceModal
            open={open}
            className="company-settings-modal"
            title={
                <div className="company-settings-title">
                    <Tag color="processing">SETTINGS</Tag>
                    <Typography.Text strong ellipsis className="company-settings-title-name">
                        {selectedCompany?.name || 'Company Settings'}
                    </Typography.Text>
                    {selectedCompany ? (
                        <Space size="middle" className="company-settings-title-meta">
                            <span><AppstoreOutlined /> {enabledModules.length || 0} modules</span>
                            <span><NumberOutlined /> ID: {selectedCompany.id}</span>
                        </Space>
                    ) : null}
                </div>
            }
            onClose={handleClose}
            footer={
                <div className="company-settings-footer">
                    <Button onClick={handleClose}>Close</Button>
                </div>
            }
        >
            {selectedCompany ? (
                <div className="company-settings-workspace">
                    <Tabs
                        defaultActiveKey="settings"
                        className="company-settings-tabs"
                        items={[
                            {
                                key: 'settings',
                                label: 'Settings',
                                children: (
                                    <GeneralSettingsTab
                                        company={selectedCompany}
                                        canManage={canManageCompany}
                                    />
                                ),
                            },
                            {
                                key: 'overview',
                                label: 'Overview',
                                children: (
                                    <CompanyOverviewTab
                                        company={selectedCompany}
                                        open={open}
                                        onDeleteClick={() => setIsDeleteModalOpen(true)}
                                    />
                                ),
                            },
                            {
                                key: 'members',
                                label: 'Members',
                                children: (
                                    <CompanyMembersTab
                                        company={selectedCompany}
                                        canManage={canManageCompany}
                                    />
                                ),
                            },
                            {
                                key: 'shopify',
                                label: 'Shopify',
                                children: (
                                    <CompanyShopifyTab
                                        company={selectedCompany}
                                        canManage={canManageCompany}
                                        shopify={selectedShopify}
                                        onOpenShopifyModal={() => setShopifyModalOpen(true)}
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
            ) : (
                <div className="company-settings-empty">
                    <Card>
                        <Typography.Text type="secondary">
                            No company is selected. Choose a company from the top bar to manage its settings.
                        </Typography.Text>
                    </Card>
                </div>
            )}
            <DeleteCompanyModal open={isDeleteModalOpen} handleClose={() => setIsDeleteModalOpen(false)} selectedCompany={selectedCompany ?? undefined} />
            <AddShopifyShopModal
                open={shopifyModalOpen}
                handleClose={() => setShopifyModalOpen(false)}
                companyId={selectedCompany?.id}
                companyName={selectedCompany?.name}
                replaceExisting={Boolean(selectedCompany?.modules?.shopify)}
            />
        </WorkspaceModal>
    );
};

export default CompanySettingsModal;
